/**
 * 认证模块接口目录
 * 本文件作为认证模块内所有API请求的统一入口
 * 只负责发送HTTP请求，不处理业务逻辑
 * @author 系统
 * @date 2024-12-19
 */

import { isNewApiEnabled, requestByModule } from '@/utils/request'

// ==================== 类型定义 ====================

/**
 * 登录请求参数接口
 */
export interface ILoginParams {
  username: string
  password: string
}

/**
 * 短信验证码登录请求参数接口
 */
export interface IOldLoginResponse {
  id: number
  username: string
  nickname: string
  phone?: string
  email?: string
  isSuperAdmin: number // 1表示超级管理员，0表示普通用户
  status: number
  passwordV: number
  lastLoginAt: string
  tokenName: string
  tokenValue: string
  roles: Array<{
    id: number
    name: string
    remark: string
  }>
  permInfo: {
    perms: Array<{
      id: number
      type: 'module' | 'menu' | 'permission'
      code?: string
      name: string
      icon?: string
      description: string
      module: string
      parentId?: number
      orderNum: number
      router: string
      viewPath: string
      createdAt: string
      updatedAt: string
    }>
    permCodes?: string[]
  }
}

export interface IUserInfoResponse {
  id: number
  username: string
  realName?: string
  phone?: string
  email?: string
  avatar?: string
  accountType?: string
  tenantId?: number
  isAdmin?: boolean
  isTenantAdmin?: boolean
  roles?: string[]
  permissions?: string[]
  lastLoginTime?: string
}

export interface ILoginResponse {
  token: string
  tokenType?: string
  expiresIn?: number
  userInfo: IUserInfoResponse
}

// ==================== 认证接口 ====================

/**
 * 用户登录（账号密码）
 * @param params 登录参数
 * @returns 登录结果
 */
export const login = (params: ILoginParams): Promise<ILoginResponse> => {
  const url = isNewApiEnabled('auth') ? '/api/user/login' : '/admin/login'
  return requestByModule<ILoginResponse>('auth', {
    url,
    method: 'POST',
    data: params
  })
}

/**
 * 用户登出
 * @returns 登出结果
 */
export const logout = (): Promise<Record<string, any>> => {
  const url = isNewApiEnabled('auth') ? '/api/user/logout' : '/admin/logout'
  return requestByModule<Record<string, any>>('auth', {
    url,
    method: 'POST'
  })
}

/**
 * 获取当前用户信息
 * @returns 用户信息
 */
export const getCurrentUserInfo = (): Promise<IUserInfoResponse> => {
  return requestByModule<IUserInfoResponse>('auth', {
    url: '/api/user/info',
    method: 'GET'
  })
}

/**
 * 获取当前用户权限列表
 * @returns 权限列表
 */
export const getCurrentUserPermissions = (): Promise<string[]> => {
  return requestByModule<string[]>('auth', {
    url: '/api/user/permissions',
    method: 'GET'
  })
}

/**
 * 刷新令牌
 * @returns 登录响应
 */
export const refreshAccessToken = (): Promise<ILoginResponse> => {
  return requestByModule<ILoginResponse>('auth', {
    url: '/api/user/refresh',
    method: 'POST'
  })
}
